import { useEffect, useState, useRef } from "react";
import { ChevronRight } from "@carespace-icons/arrows/chevronRight";
import { ChevronLeft } from "@carespace-icons/arrows/chevronLeft";
import { useTranslation } from "react-i18next";
import { useTypedDispatch, useTypedSelector } from "@stores/index";
import { selectExercise, selectProgram, setIsProgramPaused } from "@stores/aiAssistant/program";
import { Button } from "antd";
import { setActiveTab, setProgramStateId, setStateId } from "@stores/aiAssistant/aiAssistant";
import { useNavigate } from "react-router-dom";
import { useBlockNavigation } from "@atoms/ABlockNavigation";
import { router } from "@routers/routers";
import { Link01 } from "@carespace-icons/general/link01";
import { getUserById, saveState } from "@stores/contacts/contacts";
import { Calendar } from "@carespace-icons/time/calendar";
import { INTAKE_STEPS } from "@stores/constants";
import { IProgramItem, IProgramPatient } from "@stores/interfaces";
import MyProgramUnavailableScreen from "./MyProgramUnavailableScreen";


export default function MyProgramsPatientOnboard(props: IProgramPatient) {
  const { programData, fetchHomeData, pagination } = props
  const [activeImage, setActiveImage] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const user = useTypedSelector(state => state.user);
  const { selectedUser } = useTypedSelector(state => state.contacts.main);
  const navigate = useNavigate();
  const { clearNavigation } = useBlockNavigation();
  const dispatch = useTypedDispatch();
  const [arrowShow, setArrowShow] = useState(false);
  const isProgramPaused = useTypedSelector((state) => state.aiAssistant.program.isProgramPaused)

  useEffect(() => {
    fetchHomeData(currentPage);
  }, [currentPage, selectedUser]);

  const handleNextPage = () => {
    pagination.pageCount != currentPage && setCurrentPage(prev => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      const currentPosition = scrollRef.current?.scrollLeft;
      const targetPosition = currentPosition - 400;
      scrollToPosition(targetPosition, 300);
    }
    if (scrollRef.current?.scrollLeft === 0) {
      handlePreviousPage();
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      const currentPosition = scrollRef.current?.scrollLeft;
      const targetPosition = currentPosition + 400;
      scrollToPosition(targetPosition, 300);
    }
    if (
      scrollRef.current &&
      scrollRef.current.scrollLeft + scrollRef.current.offsetWidth >= scrollRef.current.scrollWidth
    ) {
      handleNextPage();
    }
  };

  const scrollToPosition = (targetPosition: number, duration: number) => {
    const startTime = performance.now();
    const start = scrollRef.current?.scrollLeft;
    const scroll = (timestamp: number) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = start! + (targetPosition - start!) * progress;
      }
      if (progress < 1) {
        requestAnimationFrame(scroll);
      }
    };
    requestAnimationFrame(scroll);
  };

  const handleImageClick = (index: number) => {
    setActiveImage(index);
  }

  const handleViewHistory = async (item: { id: string; }) => {
    const id = user?.isPhysioterapist ? selectedUser?.id : user?.id;
    clearNavigation();
    dispatch(
      saveState({
        activeTab: "Let's Move",
        userId: id,
      }),
    );
    navigate(`/${id}${router.AIASSISTANT_LIST_SESSIONS}`);
    dispatch(setActiveTab('listSessions'));
    await dispatch(getUserById(id));
    dispatch(setStateId(item?.id));
    dispatch(setProgramStateId(item?.id));
  };

  const startProgramSession = (item: IProgramItem) => {
    dispatch(selectProgram(item));
    dispatch(selectExercise(item?.exercises));
    clearNavigation();
    navigate(
      `/${user.isPhysioterapist ? selectedUser.id : user.id}${router.AIASSISTANT_PROGRAM_START}`,
    );
    dispatch(setActiveTab(''));
  };

  useEffect(() => {
    if(user?.profile?.isWellnessCheck) {
      dispatch(setIsProgramPaused(true))
    }
  },[user])

  return (
    <>
      <div className={`${programData?.length > 0 ? 'program-main-container-css' : 'program-main-container-without-data'}`} style={{ position: 'relative', backgroundColor: 'var(--card-bg-color)' }}>
        <p className="program-inner-container-css" style={{ color: "var(--text-color-root)" }}>
          <span>{t('Patient.data.newDashboard.myProgram')}</span>
          <span className="program-title-tips" style={{ color: "var(--gray-800)" }}>
            {t('Patient.data.newDashboard.myProgramTips')}
          </span>
        </p>
        {programData?.length > 0 ?
          <div className="image-navigation-container">
            <div className="horizontal-scroll-container relative" ref={scrollRef}>
              {programData?.map((item: IProgramItem, index) => {
                return (
                  <div
                    key={index}
                    className={`custom-image-container-form ${activeImage === index ? "w-h-330" : "w-h-300"}`}
                    onMouseOver={() => !arrowShow && handleImageClick(index)}
                    onMouseLeave={() => setActiveImage(null)}
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/${user?.id}/overview`, {
                        state: {
                          selectedProgram: item,
                          domain: INTAKE_STEPS.INTAKE_PROGRAMS
                        },
                      });
                    }}
                  >
                    <div className="image-wrapper-form">
                      {item?.thumbnail || item?.exercises[0]?.image ? (
                        <img
                          src={item?.thumbnail ? item?.thumbnail : item?.exercises[0] && item?.exercises[0]?.image}
                          alt={item?.exercises[0]?.image}
                          className="image-thumbnail-program saturate-image"
                        />
                      ) : (
                        <video
                          preload="metadata"
                          src={item?.exercises[0]?.exerciseLibrary?.videoUrl}
                          className="video-thumbnail-program saturate-image"
                        />
                      )}
                      <div className="overlay-content-css">
                        <p className={`program-custom-btn-design ${activeImage === index ? "mb-2" : ""}`}>{item?.name}</p>
                        {activeImage != null && <div className="hover-details extra-details-div">
                          <Button
                            className={`my-program-patient-custom-style`}
                            icon={<Link01 height={10} width={10} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewHistory(item);
                            }}
                          >
                            {t('Patient.data.newDashboard.viewHistory')}
                          </Button>
                        </div>}
                        <Button
                          className='start-session-css program-inner-btn-onboard-css'
                          style={{ border: "0px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            startProgramSession(item);
                          }}
                        >
                          {t('Patient.data.myProgress.rehab.startSession')}
                        </Button>
                        <div className="hover-details">
                          {item?.exercises && (
                            <><div className="exercise-count">
                              {`${item?.exercises.length} ${t(
                                `Patient.data.newDashboard.${item?.exercises.length === 1 ? 'exercise' : 'exercises'}`
                              )}`}
                            </div>
                              <div className="exercise-count-duration">
                                <Calendar height={20} width={20} /> {item?.duration}{" "}
                                {item?.durationType
                                  ? item?.duration! > 1
                                    ? item.durationType.charAt(0).toUpperCase() + item.durationType.slice(1)
                                    : item.durationType.charAt(0).toUpperCase() + item.durationType.slice(1).replace(/s$/, "")
                                  : ""}
                              </div></>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="navigation-buttons" style={{ top: '40%' }}>
              {pagination?.totalCount > 4 && <>
                <span className="nav-button left"
                  onClick={scrollLeft}>
                  <ChevronLeft width={30} height={30} />
                </span>
                <span
                  className="nav-button right"
                  onClick={scrollRight}>
                  <ChevronRight width={30} height={30} />
                </span>
              </>}
            </div>
          </div> :
          <div style={{ backgroundColor: '#F2F4F7', padding: '20px', borderRadius: '12px' }}>
            <div className="empty-program-onboard-container-css">
              <img src="/images/dashboard/suggested-program-empty.svg" alt="program-empty" />
              <div className="empty-program-inner-onboard-text-css text-gray-blue-700">
                <p>{t('Patient.data.newDashboard.programEmptyTitle')}</p>
                <p>{t('Patient.data.newDashboard.programEmptyDescription')}</p>
              </div>
            </div>
          </div>
        }
        {isProgramPaused && <MyProgramUnavailableScreen /> }
      </div>
    </>
  );
}
